import type {
  EntityBody,
  KineticCoreState,
} from "@cloudnest/kinetic-core-engine";
import type { PieceTypeInitConfig } from "./config/types";

interface GameInfoProps {
  readonly gameState: KineticCoreState;
  readonly selectedEntity: EntityBody | null;
  readonly pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>;
  readonly isAnimating: boolean;
  readonly lastError: string | null;
  readonly isMobile: boolean;
  readonly canConcede?: boolean;
  readonly onConcede: () => void;
  readonly onNewGame: () => void;
}

const containerStyle: React.CSSProperties = {
  padding: "16px",
  backgroundColor: "var(--cn-alkagi-surface)",
  borderRadius: "8px",
  boxShadow: "0 2px 8px var(--cn-alkagi-stone-shadow)",
  minWidth: "260px",
};

const containerStyleMobile: React.CSSProperties = {
  ...containerStyle,
  width: "100%",
  padding: "12px",
};

const turnStyle: React.CSSProperties = {
  fontSize: "14px",
  color: "var(--cn-alkagi-text-muted)",
  marginBottom: "4px",
};

const playerStyle: React.CSSProperties = {
  fontSize: "20px",
  fontWeight: "bold",
  marginBottom: "14px",
};

const countRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "8px",
  fontSize: "14px",
};

const detailStyle: React.CSSProperties = {
  marginTop: "14px",
  paddingTop: "10px",
  borderTop: "1px solid var(--cn-alkagi-surface-border)",
};

const errorStyle: React.CSSProperties = {
  color: "var(--cn-alkagi-error)",
  fontSize: "13px",
  marginTop: "12px",
  padding: "8px",
  backgroundColor: "var(--cn-alkagi-error-bg)",
  borderRadius: "4px",
};

const buttonStyle: React.CSSProperties = {
  padding: "8px 16px",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "14px",
  marginTop: "8px",
  width: "100%",
};

function countRemaining(
  entities: ReadonlyArray<EntityBody>,
  owner: string,
): number {
  return entities.filter(
    (entity) => !entity.eliminated && entity.owner === owner,
  ).length;
}

export function GameInfo({
  gameState,
  selectedEntity,
  pieceTypesById,
  isAnimating,
  lastError,
  isMobile,
  canConcede = true,
  onConcede,
  onNewGame,
}: GameInfoProps) {
  const [playerOne, playerTwo] = gameState.participants;

  const playerOneCount = countRemaining(gameState.entities, playerOne.id);
  const playerTwoCount = countRemaining(gameState.entities, playerTwo.id);

  const currentParticipant =
    gameState.participants.find(
      (participant) => participant.id === gameState.currentParticipant,
    ) ?? playerOne;

  const selectedPieceType = selectedEntity
    ? pieceTypesById.get(selectedEntity.archetype)
    : null;

  return (
    <div style={isMobile ? containerStyleMobile : containerStyle}>
      <div data-testid="turn-display" style={turnStyle}>
        Turn {gameState.turn}
      </div>
      <div
        data-testid="current-player-display"
        style={{
          ...playerStyle,
          fontSize: isMobile ? "18px" : "20px",
          marginBottom: isMobile ? "12px" : "14px",
          color:
            currentParticipant.id === playerOne.id
              ? "var(--cn-alkagi-faction-han)"
              : "var(--cn-alkagi-faction-cho)",
        }}
      >
        {currentParticipant.name}
        {isAnimating ? " (resolving...)" : " turn"}
      </div>

      <div style={countRowStyle}>
        <span style={{ color: "var(--cn-alkagi-faction-han)" }}>
          {playerOne.name} pieces:
        </span>
        <span
          data-testid="player-one-piece-count"
          style={{ fontWeight: "bold" }}
        >
          {playerOneCount}
        </span>
      </div>

      <div style={countRowStyle}>
        <span style={{ color: "var(--cn-alkagi-faction-cho)" }}>
          {playerTwo.name} pieces:
        </span>
        <span
          data-testid="player-two-piece-count"
          style={{ fontWeight: "bold" }}
        >
          {playerTwoCount}
        </span>
      </div>

      {selectedPieceType && selectedEntity && (
        <div style={detailStyle}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              color: "var(--cn-alkagi-text-muted)",
            }}
          >
            Selected Piece Type
          </div>
          <div
            style={{
              fontSize: "15px",
              fontWeight: "bold",
              marginTop: "4px",
              color: "var(--cn-alkagi-text)",
            }}
          >
            {selectedPieceType.name}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--cn-alkagi-text-muted)",
              marginTop: "6px",
            }}
          >
            Size: {selectedPieceType.size.toFixed(2)} | Weight:{" "}
            {selectedPieceType.weight.toFixed(2)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--cn-alkagi-text-muted)",
              marginTop: "2px",
            }}
          >
            Config Count per Side: {selectedPieceType.count}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--cn-alkagi-text-muted)",
              marginTop: "2px",
            }}
          >
            Connected Augments: {selectedPieceType.connectedAugments.join(", ")}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--cn-alkagi-text-muted)",
              marginTop: "4px",
            }}
          >
            Entity ID: {selectedEntity.id}
          </div>
        </div>
      )}

      {!isMobile && (
        <div
          style={{
            marginTop: "16px",
            fontSize: "13px",
            color: "var(--cn-alkagi-text-muted)",
          }}
        >
          Select your piece, drag, and release to apply impulse.
        </div>
      )}

      {lastError && (
        <div data-testid="error-display" style={errorStyle} role="alert">
          {lastError}
        </div>
      )}

      <button
        data-testid="concede-btn"
        type="button"
        style={{
          ...buttonStyle,
          backgroundColor: "var(--cn-alkagi-surface-secondary)",
          color: "var(--cn-alkagi-text)",
        }}
        onClick={onConcede}
        disabled={!canConcede || isAnimating || gameState.phase !== "RUNNING"}
      >
        Concede
      </button>
      <button
        data-testid="new-game-btn"
        type="button"
        style={{
          ...buttonStyle,
          backgroundColor: "var(--cn-alkagi-faction-han)",
          color: "var(--cn-alkagi-surface)",
        }}
        onClick={onNewGame}
      >
        New Game
      </button>
    </div>
  );
}
